import { useEffect, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { Hub } from "aws-amplify/utils";
import { getCurrentUser } from "aws-amplify/auth";
import { logger } from "@amzn/afxtaco-utilities";
import { signOut } from "../authUtils";
import { COGNITO_AUTHENTICATION_CODES } from "../constants";
import { setSignedIn } from "../../../redux/reducers/Auth";
import { setUserProfile } from "../../../redux/reducers/UserProfile";

/**
 * Custom hook that listens to Amplify Hub authentication events and handles
 * sign in/sign out operations based on Cognito authentication codes.
 *
 * Automatically updates Redux state for Auth and UserProfile on sign in/out.

 *
 * @example
 * ```typescript
 * function App() {
 *   useAuthentication();
 *
 *   return <div>Your App</div>;
 * }
 * ```
 */
export function useAuthentication() {
  const dispatch = useDispatch();

  // Used to trigger initial setting of user if already signed in before this
  // hook is first instantiated.
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  /**
   * Handles the sign in event by fetching user data and updating Redux state. Triggered by actual sign in event
   */
  const handleSignIn = useCallback(async (): Promise<void> => {
    try {
      // Get current user - this uses the ID token which contains user attributes
      const { username, userId, signInDetails } = await getCurrentUser();

      if (!username) {
        throw new Error("Failed to get current user after sign in");
      }

      // Create user profile from getCurrentUser response
      const userProfile = {
        sub: userId,
        email: username, // For federated users, username is typically the email
        // Add any other attributes from signInDetails if available
        ...signInDetails,
      };

      // Update Redux state
      dispatch(setSignedIn(true));
      dispatch(setUserProfile(userProfile));
    } catch (error) {
      logger.error({
        loggerMsg: "Error handling sign in event",
        error,
      });
      throw error;
    }
  }, [dispatch]);

  /**
   * Handles the sign out event by calling signOut and updating Redux state
   */
  const handleSignOut = useCallback(async (): Promise<void> => {
    try {
      // Sign out the user
      const redirectLocation = await signOut();

      // Update Redux state
      dispatch(setSignedIn(false));
      dispatch(setUserProfile({}));

      logger.info({
        loggerMsg: "User signed out successfully and Redux state cleared",
        redirectLocation,
      });
    } catch (error) {
      logger.error({
        loggerMsg: "Error handling sign out event",
        error,
      });
      throw error;
    }
  }, [dispatch]);

  useEffect(() => {
    // Subscribe to Hub authentication events
    const unsubscribe = Hub.listen(
      "auth",
      async ({
        payload,
      }: {
        payload: { event: COGNITO_AUTHENTICATION_CODES; data: string };
      }) => {
        const { event } = payload;

        switch (event) {
          case COGNITO_AUTHENTICATION_CODES.SIGN_IN:
            await handleSignIn();
            break;

          case COGNITO_AUTHENTICATION_CODES.SIGN_IN_WITH_REDIRECT:
            logger.info({
              loggerMsg: "Sign in with redirect initiated",
            });
            // This event fires when the redirect to the OAuth provider starts
            // No action needed here - the actual sign in will trigger SIGN_IN event
            break;

          case COGNITO_AUTHENTICATION_CODES.SIGN_OUT:
            await handleSignOut();
            break;

          case COGNITO_AUTHENTICATION_CODES.CUSTOM_OAUTH_STATE:
            logger.info({
              loggerMsg: "Custom OAuth state received",
            });
            try {
              const customState = JSON.parse(payload.data as string);
              if (customState?.startLocation) {
                const targetUrl = new URL(customState.startLocation);
                // Only navigate if it's not already the homepage
                const targetPath =
                  targetUrl.pathname + targetUrl.search + targetUrl.hash;
                const currentPath =
                  window.location.pathname +
                  window.location.search +
                  window.location.hash;
                if (targetPath !== "/" && targetPath !== currentPath) {
                  window.location.replace(customState.startLocation);
                }
              }
            } catch (e) {
              logger.error({
                loggerMsg: "Failed to parse custom OAuth state",
                error: e,
              });
            }
            break;

          case COGNITO_AUTHENTICATION_CODES.SIGN_IN_WITH_REDIRECT_FAILURE:
            logger.error({
              loggerMsg: "Sign in with redirect failed",
              event,
              payload,
            });
            // The OAuth callback failed - this could be due to user cancellation,
            // invalid state, or other OAuth errors. The app should handle this
            // by redirecting to sign in again or showing an error message.
            break;

          default:
            logger.info({
              loggerMsg: "Unhandled authentication event",
              event,
            });
        }
      },
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [handleSignIn, handleSignOut]);

  // Signs user in on first run of this hook, since the Amplify Hub listener
  // will miss the initial authentication which occurs before this hook is
  // first instantiated.
  // IMPORTANT: Skip this during OAuth callback - let Amplify process the callback
  // and emit the SIGN_IN event through the Hub instead.
  useEffect(() => {
    if (!isInitialized) {
      // Check if we're in an OAuth callback flow
      const params = new URLSearchParams(window.location.search);
      const isOAuthCallback = params.has("code") || params.has("error");

      if (isOAuthCallback) {
        logger.info({
          loggerMsg:
            "OAuth callback detected in useAuthentication, waiting for Hub SIGN_IN event",
        });
        setIsInitialized(true);
        return;
      }

      // Not an OAuth callback, so try to initialize user if already authenticated
      handleSignIn().catch((e) => {
        logger.error({
          loggerMsg: "Failed to initialize user",
          e,
        });
      });
      setIsInitialized(true);
    }
  }, [handleSignIn, isInitialized, setIsInitialized]);
}
